import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from "react";

export interface ArrayState<
  T extends { id: IdType },
  IdType extends string | number = T["id"],
> {
  add(item: T): IdType;
  remove(id: IdType): Promise<{ found: boolean }>;
  remove(item: T): Promise<{ found: boolean }>;
  update(
    id: IdType,
    updater: T | ((prev: T) => T),
  ): Promise<{ found: boolean }>;
  update(item: T, updater: T | ((prev: T) => T)): Promise<{ found: boolean }>;
  update(item: T): Promise<{ found: boolean }>;
  has(item: T): boolean;
  has(id: IdType): boolean;
  set: React.Dispatch<SetStateAction<T[]>>;
  __currentLength: number;
}

export function useArrayState<
  T extends { id: IdType },
  IdType extends string | number = T["id"],
>(
  initialValue: T[] | (() => T[]) = [],
  actions: {
    onChange?: (items: T[]) => void;
    onAdd?: (item: T, items: T[]) => void;
    onRemove?: (item: T, items: T[]) => void;
    onUpdate?: (oldItem: T, newItem: T, items: T[]) => void;
  } = {},
): [T[], ArrayState<T, IdType>] {
  const insertedCount = useRef<number>(0);
  const { onAdd, onRemove, onUpdate, onChange } = actions;
  const [state, _setState] = useState<T[]>(initialValue);
  const [pendingActions, setPendingActions] = useState<VoidFunction[]>([]);
  const stateRef = useRef<T[]>(state);

  stateRef.current = state;

  const setState = useCallback<typeof _setState>(
    (handler) => {
      const res =
        handler instanceof Function ? handler(stateRef.current) : handler;
      stateRef.current = res;
      _setState(res);
    },
    [_setState],
  );

  useEffect(() => {
    if (!pendingActions.length) return;
    const p = pendingActions;
    setPendingActions([]);
    p.forEach((v) => v());
  }, [pendingActions]);

  const add = useCallback(
    (item: T) => {
      setState((prev) => {
        const newState = [...prev, item];
        insertedCount.current++;
        setPendingActions((prev) => [
          ...prev,
          () => {
            onAdd?.(item, newState);
            onChange?.(newState);
          },
        ]);
        return newState;
      });
      return item.id;
    },
    [setState, onAdd, onChange],
  );

  const remove = useCallback(
    (item: IdType | T) => {
      return new Promise<{ found: boolean }>((resolve, reject) => {
        setState((prev) => {
          try {
            const id = getId(item);
            const newState = prev.filter((v) => v.id !== id);
            const found = newState.length !== prev.length;
            if (found) {
              insertedCount.current--;
            }
            if (found && (onRemove || onChange)) {
              const removedItem = prev.find((v) => v.id === id);
              if (removedItem) {
                setPendingActions((prev) => [
                  ...prev,
                  () => {
                    onRemove?.(removedItem, newState);
                    onChange?.(newState);
                  },
                ]);
              }
            }
            resolve({ found });
            return newState;
          } catch (e) {
            reject(e);
            return prev;
          }
        });
      });
    },
    [setState, onRemove, onChange],
  );

  const update = useCallback(
    (item: IdType | T, updater?: T | ((item: T) => T)) => {
      if (updater == undefined) updater = item as T;
      return new Promise<{ found: boolean }>((resolve, reject) => {
        setState((prev) => {
          try {
            const id = getId(item);
            let found = false;
            let oldItem: T | undefined = undefined;
            let updatedItem: T | undefined = undefined;
            const newState = prev.map((v) => {
              if (v.id === id) {
                found = true;
                oldItem = v;
                updatedItem =
                  typeof updater === "function" ? updater(v) : updater;
                return updatedItem;
              }
              if (found && oldItem && updatedItem && (onUpdate || onChange)) {
                setPendingActions((prev) => [
                  ...prev,
                  () => {
                    onUpdate?.(oldItem!, updatedItem!, newState);
                    onChange?.(newState);
                  },
                ]);
              }
              return v;
            });
            resolve({ found });
            return newState;
          } catch (e) {
            reject(e);
            return prev;
          }
        });
      });
    },
    [setState, onUpdate, onChange],
  );

  const has = useCallback(
    (item: IdType | T) => {
      return !!state.find((v) => v.id === getId(item));
    },
    [state],
  );

  const set = useCallback(
    (newState: T[] | ((prev: T[]) => T[])) => {
      setState((prev) => {
        const s = typeof newState === "function" ? newState(prev) : newState;
        insertedCount.current = s.length;
        setPendingActions((prev) => [
          ...prev,
          () => {
            onChange?.(s);
          },
        ]);
        return s;
      });
    },
    [setState, onChange],
  );

  const stateActions = useMemo(
    () => ({
      add,
      remove,
      update,
      set,
      has,
      __currentLength: insertedCount.current,
    }),
    [add, remove, update, set, has],
  );

  stateActions.__currentLength = insertedCount.current;

  return [stateRef.current, stateActions];
}

function getId<IdType extends number | string>(item: IdType | { id: IdType }) {
  return typeof item === "object" ? item.id : item;
}
